import type { MiddlewareFunction } from 'react-router'

import { getRequestJobsServiceByJobUuid } from '@capture/client-api/src/orval'
import { sanitisePollHost } from '@capture/client/API/apiUtils'
import {
    getAuthToken,
    getStoredServiceDict,
    overrideAccountFromSearchParams,
    resolveLoginHost,
    type ServiceDict,
    storeServiceDict,
} from '@capture/client/API/externals'
import type { ServiceDictResponse } from '@capture/client/API/services/LoginService'
import { initializeSessionServiceProvider } from '@capture/client/API/SessionServiceProvider'
import { initializeQueryClientSession } from '@capture/client/query'
import { b64ToUuid } from '@capture/client/utilities/uuid'

const getHostDirectoryFromServiceDictResponse = (service: ServiceDictResponse): ServiceDict => ({
    appHost: service['app-host'],
    thumbHost: service['thumb-host'],
    videoHost: service['video-host'],
    pollHost: sanitisePollHost(service['poll-host']),
    downloadHost: service['download-host'],
})

const getJobIdFromParams = (params: Record<string, string | undefined>) => {
    if (params.albumIDB64) {
        return b64ToUuid(params.albumIDB64)
    }

    if (params.shareID) {
        return params.shareID
    }

    return undefined
}

/**
 * Session middleware that initializes auth credentials in the queryClient.
 *
 * This runs before any route loaders and sets up:
 * 1. queryClient session (primary source for API request builders)
 * 2. SessionServiceProvider (legacy compatibility)
 *
 * Auth state can be checked via hasStoredAuthCredentials() from @capture/client/query
 */
export const sessionMiddleware: MiddlewareFunction = async ({ params }, next) => {
    // Allow URL params to override session values (for testing/debugging)
    // Must be called before reading from sessionStorage
    overrideAccountFromSearchParams()

    const authToken = getAuthToken()
    let serviceDict = getStoredServiceDict()

    // For anonymous access (no stored serviceDict), try to fetch from job
    if (!serviceDict) {
        const jobId = getJobIdFromParams(params)

        if (jobId) {
            const {
                data: { service },
            } = await getRequestJobsServiceByJobUuid(jobId, {
                baseURL: `https://${resolveLoginHost()}/st/4`,
            })
            const hosts = getHostDirectoryFromServiceDictResponse(service)
            storeServiceDict(hosts)
            serviceDict = hosts
        }
    }

    // Initialize queryClient session - this is the primary source for API request builders
    initializeQueryClientSession({
        authToken,
        serviceDict,
    })

    // Initialize SessionServiceProvider for backward compatibility with service classes
    // TODO: Deprecate once all services are migrated to use queryClient session
    initializeSessionServiceProvider(authToken, serviceDict)

    return next()
}
